import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Hover tooltip.
 *
 * Renders into a portal rather than next to its trigger, because the places that want one
 * are inside clipping containers — the table scrolls (`overflow-auto`) and a compact cell
 * truncates (`overflow: hidden`), either of which would slice an absolutely positioned
 * bubble in half. Fixed coordinates off the trigger's rect sidestep both.
 *
 * It also replaces the native `title` attribute where the delay matters: browsers wait
 * roughly a second before showing one and that is not configurable.
 */

/** Distance between the trigger and the bubble. */
const GAP = 6;
/** Keep the bubble this far from the viewport edge when it has to shift sideways. */
const MARGIN = 8;

interface TooltipProps {
  /** Bubble contents. Nothing is rendered when this is empty, so callers can pass a maybe-value. */
  content: ReactNode;
  children: ReactNode;
  /** Milliseconds to hover before the bubble appears. */
  delay?: number;
  /** Class for the inline wrapper around `children`. */
  className?: string;
}

type Anchor = { cx: number; top: number; bottom: number };
type Placement = { left: number; top: number; below: boolean };

export function Tooltip({ content, children, delay = 200, className }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const id = useId();

  const hide = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setAnchor(null);
    setPlacement(null);
  }, []);

  const show = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setAnchor({ cx: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
    }, delay);
  }, [delay]);

  // Measure the bubble once it exists, then place it: above the trigger by default, below
  // when there is no room, and nudged sideways so it never runs off the viewport.
  useLayoutEffect(() => {
    if (!anchor) return;
    const bubble = bubbleRef.current;
    if (!bubble) return;

    const { width, height } = bubble.getBoundingClientRect();
    const below = anchor.top - height - GAP < MARGIN;
    const maxLeft = window.innerWidth - width / 2 - MARGIN;
    setPlacement({
      left: Math.min(Math.max(anchor.cx, width / 2 + MARGIN), Math.max(maxLeft, width / 2 + MARGIN)),
      top: below ? anchor.bottom + GAP : anchor.top - GAP,
      below,
    });
  }, [anchor, content]);

  // The coordinates are a snapshot, so anything that moves the trigger retires the bubble.
  useEffect(() => {
    if (!anchor) return;
    const onScroll = () => hide();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [anchor, hide]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  if (!content) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={anchor ? id : undefined}
      >
        {children}
      </span>
      {anchor &&
        createPortal(
          <div
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className="fixed z-100 px-2.5 py-1.5 text-xs text-dark-text-primary bg-dark-elevated border border-dark-border rounded-sm shadow-lg w-max max-w-xs pointer-events-none"
            style={{
              left: placement?.left ?? anchor.cx,
              top: placement?.top ?? anchor.top,
              transform: `translate(-50%, ${placement?.below ? '0' : '-100%'})`,
              // Hidden for the frame between mounting and being measured, so it never
              // flashes in the wrong spot.
              visibility: placement ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
